/**
 * Smoosh Starter Kit PDP — JavaScript
 * Version: 3.0 — Build V1.25
 *
 * Architecture — two independent IIFEs:
 *
 *   LAYER 1 — Global cart maintenance (every page)
 *     • Bundle detection (isBundleItem), kit-card injection into cart drawer
 *     • Re-inject via MutationObserver + Dawn pub/sub (survives SPA navigation)
 *     • Suppress/release machinery so ATC handler can lock out re-injection
 *     • Discount helpers: apply/clear via /cart/update.js (Editions '25 API)
 *     • Exposes: _skInjectKitCard, _skSetSuppressed, _skApplyDiscount,
 *       _skClearDiscount
 *
 *   LAYER 2 — PDP UI (only when .starter-kit-pdp exists)
 *     • State (mode, scrubber variant, soap slots), render loop
 *     • Plan toggle, scrubber swatch, soap tile interactions
 *     • Thumbnail gallery with scroll progress bar
 *     • ATC handler: builds line items, posts to /cart/add.js, applies
 *       discount (subscribe mode), re-renders cart drawer via Section API
 *
 *   Both layers have window-level sentinels (_skLayer1Loaded, _skLayer2Loaded)
 *   to prevent double-execution if Shopify loads the script asset twice.
 *
 * Key behaviors:
 *   • Subscribe mode: soaps get selling_plan IDs; scrubber uses SK variant
 *     (resolved via skScrubberVariantMap); discount code auto-applied
 *   • One-time mode: standard variants, no selling plans, no discount
 *   • Kit card in cart drawer: groups bundle items by _bundle_key, shows
 *     strikethrough pricing when discount is active, "Remove kit" zeroes all
 *   • CMS labels: kit card text pulled from StarterKitData.cms with defaults
 */

// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 1 — Global cart-maintenance (every page)
// ═══════════════════════════════════════════════════════════════════════════════
(function () {
  'use strict';

  // ─── Sentinel: skip if Layer 1 already loaded (script executed twice) ────
  if (window._skLayer1Loaded) return;
  window._skLayer1Loaded = true;

  const BUNDLE_KEY_PREFIX = 'smoosh-starter-kit';
  window._skBundleKeyPrefix = BUNDLE_KEY_PREFIX;

  // ─── isBundleItem ──────────────────────────────────────────────────────────
  function isBundleItem(item) {
    return item.properties &&
      typeof item.properties['_bundle_key'] === 'string' &&
      item.properties['_bundle_key'].startsWith(BUNDLE_KEY_PREFIX);
  }

  // ─── repeatPriceCents ──────────────────────────────────────────────────────
  // What the subscribed soaps in one kit cost per repeat shipment, in cents.
  // The cart reports each plan's price per delivery in
  // selling_plan_allocation.price_adjustments; the last entry is the ongoing
  // (repeat) price. Returns null if any subscribed soap doesn't report one, so
  // the card shows no price rather than a wrong one.
  function repeatPriceCents(soaps) {
    let total = 0;
    for (const item of soaps) {
      const alloc = item.selling_plan_allocation;
      if (!alloc) continue;
      const adj = alloc.price_adjustments;
      if (!Array.isArray(adj) || !adj.length) return null;
      const unit = adj[adj.length - 1].price;
      if (typeof unit !== 'number' || !isFinite(unit)) return null;
      total += unit * item.quantity;
    }
    return total > 0 ? total : null;
  }

  // ─── savedPercent / kitBadgeText ───────────────────────────────────────────
  // The kit badge's percentage is derived from the same two prices the card
  // shows (struck-through original vs. what's charged), never typed in, so it
  // follows price changes and differs correctly between kits. Truncated, not
  // rounded: 38/67 = 56.7% advertises as 56%.
  function savedPercent(origCents, totalCents) {
    if (!(origCents > totalCents) || !(origCents > 0)) return 0;
    return Math.floor((origCents - totalCents) * 100 / origCents);
  }

  // Keeps the wording from settings ("You Saved {pct}%", or an older
  // "You Saved 56%") and swaps its number for the real one. Text with no
  // percentage in it (e.g. "↻ 90-Day Auto-ship") is left alone.
  function kitBadgeText(text, pct) {
    return String(text).replace(/\{pct\}%?|\d+(?:\.\d+)?%/, pct + '%');
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // ─── _suppressReInject with safety release ────────────────────────────────
  // While suppressed, the MutationObserver/pub-sub re-inject triggers below are
  // dropped (see _scheduleReInject). If an UNRELATED add-to-cart (e.g. the
  // standalone product page, or the cart-drawer upsell) re-renders the drawer
  // during that window, its own reinject request is silently dropped too, and
  // nothing else was going to bring the kit card back — it stays missing until
  // some future cart mutation happens to fire another observer/pub-sub event.
  // That's the root cause of the "discount box disappears" bug: it only shows
  // up when a second, independent cart mutation lands inside this window, so
  // it's timing-dependent and reproduces more on slower connections.
  //
  // Fix: always reconcile once on the way out of suppression (both the normal
  // release and the 3s safety-timer release) by re-running injectKitCard().
  // It reads the live cart from /cart.js and rebuilds from scratch, so this is
  // idempotent — safe to call even when nothing happened during the window.
  let _suppressReInject   = false;
  let _suppressReleaseTimer = null;

  function _setSuppressed(val) {
    const wasSuppressed = _suppressReInject;
    _suppressReInject = val;
    if (_suppressReleaseTimer) clearTimeout(_suppressReleaseTimer);
    if (val) {
      _suppressReleaseTimer = setTimeout(() => {
        _suppressReInject = false;
        injectKitCard();
      }, 3000);
    } else if (wasSuppressed) {
      injectKitCard();
    }
  }

  // Expose to PDP layer so the ATC handler can suppress during its own adds
  window._skSetSuppressed = _setSuppressed;

  // ─── Discount code helper ────────────────────────────────────────────────
  // CMS-driven (price code + optional free shipping code). Both codes are sent
  // as a single comma-separated value to /cart/update.js — Shopify's Cart Ajax
  // API supports multi-code stacking when Combinations settings allow it.
  // The free shipping code is unconditional in Admin; this JS gates when it
  // gets applied (only on subscription kit ATC).
  let _discountsApplied = ""; // last applied joined-codes string

  async function _applyDiscount(codeOrCodes) {
    const codes = (Array.isArray(codeOrCodes) ? codeOrCodes : [codeOrCodes])
      .map(c => (c || '').trim())
      .filter(Boolean);
    if (!codes.length) return;
    const joined = codes.join(',');
    if (_discountsApplied === joined) return;
    try {
      const res = await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ discount: joined })
      });
      if (!res.ok) throw new Error('Discount apply failed: ' + res.status);
      _discountsApplied = joined;
      console.log('[StarterKit] Discount(s) applied:', joined);
    } catch (e) {
      console.warn('[StarterKit] Discount apply error:', e);
    }
  }

  async function _clearDiscount() {
    _discountsApplied = "";
    try {
      await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ discount: '' })
      });
      console.log('[StarterKit] Discount cleared');
    } catch (e) {
      console.warn('[StarterKit] Discount clear error:', e);
    }
  }

  // Expose helpers to PDP layer
  window._skApplyDiscount  = _applyDiscount;
  window._skClearDiscount  = _clearDiscount;

  // ─── injectKitCard ─────────────────────────────────────────────────────────
  // Groups all bundle items by their unique _bundle_key and renders one card
  // per group. Works in two contexts:
  //   • Cart drawer (<cart-drawer>) — re-renders via Section API on remove
  //   • Full /cart page (<cart-items>#main-cart-items) — reloads page on remove
  function injectKitCard() {
    const _cms = (window.StarterKitData && window.StarterKitData.cms) || {};
    const kitRemove   = _cms.kitCardRemove   || 'Remove kit';

    // Build list of cart contexts to inject into
    const contexts = [];
    const drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      contexts.push({
        type:       'drawer',
        container:  drawerEl,
        rowPrefix:  '#CartDrawer-Item-',
        wrapperSel: '.drawer__cart-items-wrapper'
      });
    }
    const cartPageEl = document.querySelector('#main-cart-items');
    if (cartPageEl) {
      contexts.push({
        type:       'page',
        container:  cartPageEl,
        rowPrefix:  '#CartItem-',
        wrapperSel: '.js-contents'
      });
    }
    if (!contexts.length) return;

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const allBundleItems = cart.items.filter(isBundleItem);
        if (!allBundleItems.length) return;

        // Remove ALL kit cards from the entire document to prevent duplicates
        document.querySelectorAll('.sk-cart-kit-card').forEach(el => el.remove());

        const hasStandalone = cart.items.some(item => !isBundleItem(item));

        const groups = {};
        allBundleItems.forEach(item => {
          const key = item.properties['_bundle_key'];
          if (!groups[key]) groups[key] = [];
          groups[key].push(item);
        });

        // Inject into each context (drawer and/or /cart page)
        contexts.forEach(ctx => {
          // Re-query container in case DOM changed during async fetch
          const container = ctx.type === 'drawer'
            ? (document.querySelector('cart-drawer') || ctx.container)
            : (document.querySelector('#main-cart-items') || ctx.container);

          // Tag every bundle row so it gets hidden via CSS
          allBundleItems.forEach(item => {
            const idx = cart.items.indexOf(item) + 1;
            const row = container.querySelector(`${ctx.rowPrefix}${idx}`);
            if (row) row.setAttribute('data-bundle-key', BUNDLE_KEY_PREFIX);
          });

          const itemsWrapper = container.querySelector(ctx.wrapperSel) || container;
          const cartTable    = itemsWrapper.querySelector('table.cart-items');

          if (cartTable) {
            cartTable.style.display = hasStandalone ? '' : 'none';
          }

          Object.entries(groups).forEach(([bundleKey, bundleItems]) => {
            // A set can hold several scrubber lines (the family set builder adds
            // 1–4 scrubbers, one line per colour). Single-scrubber sets render
            // exactly as before: one row tagged ×1.
            const scrubbers   = bundleItems.filter(i => i.properties['_bundle_label'] === 'scrubber');
            const scrubber    = scrubbers[0];
            const soaps       = bundleItems.filter(i => i.properties['_bundle_label'] === 'soap');
            const isSubscribe = soaps.some(i => i.selling_plan_allocation);

            const scrubberRow = scrubbers.map(item => `
              <div class="sk-cart-kit-component">
                <img src="${item.image}" alt="${item.product_title}" class="sk-cart-kit-component__img">
                <span class="sk-cart-kit-component__name">${item.product_title} — ${item.variant_title}</span>
                <span class="sk-cart-kit-component__tag">×${item.quantity}${isSubscribe ? ' · One Time' : ''}</span>
              </div>`).join('');

            const soapRows = soaps.map(item => `
              <div class="sk-cart-kit-component">
                <img src="${item.image}" alt="${item.product_title}" class="sk-cart-kit-component__img">
                <span class="sk-cart-kit-component__name">${item.product_title}</span>
                <span class="sk-cart-kit-component__tag">×${item.quantity}</span>
              </div>`).join('');

            const totalCents     = bundleItems.reduce((sum, i) => sum + i.final_line_price, 0);
            const origCents      = bundleItems.reduce((sum, i) => sum + i.original_line_price, 0);
            const totalFormatted = (totalCents / 100).toFixed(2);
            const hasKitDiscount = origCents > totalCents;

            // Wording: this page's settings if it has them (only the kit page
            // does), else the text saved on the kit's scrubber line when it was
            // added to the cart (so home, /cart, etc. match), else a default.
            const savedProp = key => escapeHtml((scrubber && scrubber.properties && scrubber.properties[key]) || '');
            // Family sets (bundle key "smoosh-starter-kit-fmly-…") always use the
            // wording saved on their own scrubber line, so a family set and a
            // Starter Set in the same cart are each labelled correctly on any page.
            const isFamilySet = bundleKey.indexOf(BUNDLE_KEY_PREFIX + '-fmly-') === 0;
            const pick = (cmsVal, key) => isFamilySet ? (savedProp(key) || cmsVal) : (cmsVal || savedProp(key));
            const kitTitle  = pick(_cms.kitCardTitle, '_kit_card_title')   || 'Smoosh Starter Set';
            const kitRefill = pick(_cms.kitCardRefill, '_kit_card_refill') || '↻ Soaps auto-ship every 2 months';
            const savedPct  = savedPercent(origCents, totalCents);
            const savedCents = Math.max(0, origCents - totalCents);
            const savedDollars = '$' + (savedCents % 100 === 0 ? (savedCents / 100).toFixed(0) : (savedCents / 100).toFixed(2));
            const fillBadge = t => kitBadgeText(t, savedPct).split('{saved}').join(savedDollars);
            const kitSubBadge = savedPct > 0
              ? fillBadge(pick(_cms.kitCardSubBadge, '_kit_card_sub_badge') || 'You Saved {pct}%')
              : '';
            // One-time badge: only family sets send one; Starter Sets show none.
            const onceBadgeText = isFamilySet ? savedProp('_kit_card_once_badge') : '';
            const kitOnceBadge = (savedPct > 0 && onceBadgeText) ? fillBadge(onceBadgeText) : '';

            const repeatCents = isSubscribe ? repeatPriceCents(soaps) : null;
            const repeatPrice = repeatCents !== null
              ? ` <span class="sk-cart-kit-card__refill-price">· $${(repeatCents / 100).toFixed(2)}</span>`
              : '';

            const card = document.createElement('div');
            card.className = 'sk-cart-kit-card';
            card.dataset.bundleKey = bundleKey;
            card.innerHTML = `
              <div class="sk-cart-kit-card__header">
                <div class="sk-cart-kit-card__title-wrap">
                  <span class="sk-cart-kit-card__title">${kitTitle}</span>
                  ${(isSubscribe && kitSubBadge) ? `<span class="sk-cart-kit-card__sub-badge">${kitSubBadge}</span>` : ''}
                  ${(!isSubscribe && kitOnceBadge) ? `<span class="sk-cart-kit-card__sub-badge">${kitOnceBadge}</span>` : ''}
                </div>
                <div class="sk-cart-kit-card__price">${hasKitDiscount ? `<s class="sk-cart-kit-card__compare">$${(origCents / 100).toFixed(2)}</s> ` : ''}$${totalFormatted}</div>
              </div>
              <div class="sk-cart-kit-card__components">
                ${scrubberRow}
                ${soapRows}
              </div>
              <div class="sk-cart-kit-card__footer">
                ${isSubscribe ? `<span class="sk-cart-kit-card__refill-note">${kitRefill}${repeatPrice}</span>` : '<span></span>'}
                <button class="sk-cart-kit-card__remove" type="button">${(isFamilySet && savedProp('_kit_card_remove')) || kitRemove}</button>
              </div>`;

            // Remove only this kit's items
            card.querySelector('.sk-cart-kit-card__remove').addEventListener('click', async () => {
              _setSuppressed(true);

              const updates = {};
              bundleItems.forEach(item => { updates[item.key] = 0; });

              const updatedCart = await fetch('/cart/update.js', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ updates })
              }).then(r => r.json()).catch(() => null);

              // If no subscribe-mode bundles remain, clear the discount session
              if (updatedCart) {
                const skSubscribeRemains = updatedCart.items.some(item =>
                  isBundleItem(item) && item.properties['_bundle_mode'] === 'subscribe'
                );
                if (!skSubscribeRemains) {
                  await _clearDiscount();
                }
              }

              // Context-specific re-render
              if (ctx.type === 'drawer') {
                const freshDrawer = document.querySelector('cart-drawer');
                const sections = await fetch('/cart?sections=cart-drawer,cart-icon-bubble')
                  .then(r => r.json()).catch(() => null);

                if (freshDrawer && sections && typeof freshDrawer.renderContents === 'function') {
                  const isEmpty = updatedCart && updatedCart.item_count === 0;
                  freshDrawer.classList.toggle('is-empty', isEmpty);
                  freshDrawer.renderContents({ sections });

                  if (!isEmpty) {
                    setTimeout(() => {
                      injectKitCard();
                      setTimeout(() => { _setSuppressed(false); }, 1000);
                    }, 250);
                  } else {
                    setTimeout(() => { _setSuppressed(false); }, 1200);
                  }
                } else {
                  _setSuppressed(false);
                }
              } else {
                // /cart page — reload to let Shopify re-render the full page
                window.location.reload();
              }
            });

            if (cartTable) {
              if (hasStandalone) {
                cartTable.after(card);
              } else {
                cartTable.before(card);
              }
            } else {
              itemsWrapper.appendChild(card);
            }
          });
        });
      });
  }

  // Expose for PDP ATC handler to call directly after its own add
  window._skInjectKitCard = function () {
    injectKitCard();
  };

  // ─── Re-inject strategy ────────────────────────────────────────────────────
  let _reInjectTimer = null;

  function _scheduleReInject(delay) {
    if (_suppressReInject) return;
    if (_reInjectTimer) clearTimeout(_reInjectTimer);
    _reInjectTimer = setTimeout(() => {
      if (_suppressReInject) return;
      injectKitCard();
    }, delay);
  }

  // Primary: observe document.body — survives cart-drawer.replaceWith() (Path B)
  // Also watches <cart-items> for /cart page AJAX re-renders
  const obs = new MutationObserver((mutations) => {
    if (_suppressReInject) return;
    const cartDrawer = document.querySelector('cart-drawer');
    const cartPage   = document.querySelector('#main-cart-items');
    const relevant = mutations.some(m => {
      if ([...m.addedNodes, ...m.removedNodes].some(n =>
        n.nodeName === 'CART-DRAWER' || n.nodeName === 'CART-ITEMS')) return true;
      if (cartDrawer && cartDrawer.contains(m.target)) return true;
      if (cartPage && cartPage.contains(m.target)) return true;
      return false;
    });
    if (!relevant) return;
    _scheduleReInject(750);
  });
  obs.observe(document.body, { childList: true, subtree: true });

  // Secondary: Dawn pub/sub — faster 650ms path for Paths A and C
  function _attachPubSubListener() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      _scheduleReInject(650);
    });
  }

  // pub/sub globals load with defer — wait for DOMContentLoaded to be safe
  function _initialInject() {
    // Inject on initial load for BOTH contexts: /cart page (#main-cart-items)
    // AND every other page that ships a <cart-drawer>. Without this, the
    // drawer is server-rendered with exploded bundle items on PDP / collection
    // / home, and the kit card only appears after the next cart event fires.
    // injectKitCard() is a no-op if neither context is present, so this is safe.
    if (document.querySelector('cart-drawer') || document.querySelector('#main-cart-items')) {
      injectKitCard();
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      _attachPubSubListener();
      _initialInject();
    });
  } else {
    _attachPubSubListener();
    _initialInject();
  }

})();


// ═══════════════════════════════════════════════════════════════════════════════
// LAYER 2 — PDP UI (only when .starter-kit-pdp is present)
// ═══════════════════════════════════════════════════════════════════════════════
(function () {
  'use strict';

  // ─── Sentinel: skip if Layer 2 already loaded (script executed twice) ────
  if (window._skLayer2Loaded) return;
  window._skLayer2Loaded = true;

  const REQUIRED_SOAPS    = 3;
  const BUNDLE_KEY_PREFIX = window._skBundleKeyPrefix || 'smoosh-starter-kit';

  // ─── DOM ──────────────────────────────────────────────────────────────────
  const pdp = document.querySelector('.starter-kit-pdp');
  if (!pdp) return; // not the PDP page — Layer 1 still runs above

  const cms               = window.StarterKitData?.cms || {};
  const planCards         = pdp.querySelectorAll('.sk-plan-card');
  const scrubberSwatches  = pdp.querySelectorAll('.sk-color-swatch');
  const soapTiles         = pdp.querySelectorAll('.sk-soap-tile');
  const soapProgressSteps = pdp.querySelectorAll('.sk-soap-progress__step');
  const atcBtn            = pdp.querySelector('#sk-atc-btn');
  const atcBtnText        = pdp.querySelector('#sk-atc-btn-text');
  const atcNote           = pdp.querySelector('#sk-atc-note');
  const atcError          = pdp.querySelector('#sk-atc-error');
  const mainImage         = pdp.querySelector('#sk-main-image');
  const thumbTrack        = pdp.querySelector('#sk-thumb-track');
  const thumbBar          = pdp.querySelector('#sk-thumb-bar');
  const thumbBtns         = pdp.querySelectorAll('.starter-kit-pdp__thumb');
  const soapCountLabel    = pdp.querySelector('#sk-soap-count-label');
  const scrubberLabel     = pdp.querySelector('#sk-scrubber-selected-label');
  const onetimeCons       = pdp.querySelector('.sk-perks--cons');

  // ─── State ────────────────────────────────────────────────────────────────
  const state = {
    mode:              'subscribe',
    scrubberVariantId: null,
    scrubberPrice:     0,
    soapSlots:         [null, null, null]
  };

  // Pre-select first scrubber variant
  const firstSwatch = pdp.querySelector('.sk-color-swatch.is-selected');
  if (firstSwatch) {
    state.scrubberVariantId = firstSwatch.dataset.variantId;
    state.scrubberPrice     = parseInt(firstSwatch.dataset.variantPrice, 10) || 0;
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────
  function soapCount() {
    return state.soapSlots.filter(Boolean).length;
  }

  function countForVariant(vid) {
    return state.soapSlots.filter(s => s && s.variantId === vid).length;
  }

  function isComplete() {
    return !!state.scrubberVariantId && soapCount() === REQUIRED_SOAPS;
  }

  function showAtcError(msg) {
    if (atcError) {
      atcError.textContent = msg;
      atcError.hidden = false;
    } else {
      console.error('[StarterKit]', msg);
    }
  }

  function clearAtcError() {
    if (atcError) { atcError.textContent = ''; atcError.hidden = true; }
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  function render() {
    clearAtcError();

    if (soapCountLabel) soapCountLabel.textContent = `${soapCount()} of ${REQUIRED_SOAPS} selected`;

    if (scrubberLabel) {
      const active = pdp.querySelector('.sk-color-swatch.is-selected');
      scrubberLabel.textContent = active ? `— ${active.dataset.variantTitle}` : '';
    }

    const filled = soapCount();
    soapProgressSteps.forEach((stepEl, i) => {
      stepEl.classList.toggle('sk-soap-progress__step--filled', i < filled);
      stepEl.classList.toggle('sk-soap-progress__step--next',   i === filled && filled < REQUIRED_SOAPS);
    });

    soapTiles.forEach(tile => {
      const vid        = tile.dataset.variantId;
      const count      = countForVariant(vid);
      const addBtn     = tile.querySelector('.sk-soap-tile__add-btn');
      const counter    = tile.querySelector('.sk-soap-tile__counter');
      const qtyEl      = tile.querySelector('.sk-soap-tile__qty');
      const incrBtn    = tile.querySelector('.sk-soap-tile__increment');

      tile.classList.toggle('is-selected', count > 0);

      if (count > 0) {
        addBtn.hidden  = true;
        counter.hidden = false;
        if (qtyEl) qtyEl.textContent = count;
      } else {
        addBtn.hidden  = false;
        counter.hidden = true;
      }

      if (incrBtn) incrBtn.disabled = soapCount() >= REQUIRED_SOAPS;
      if (addBtn)  addBtn.disabled  = (soapCount() >= REQUIRED_SOAPS) || tile.classList.contains('sk-soap-tile--soldout');
    });

    if (onetimeCons) onetimeCons.hidden = state.mode !== 'onetime';

    const complete = isComplete();
    atcBtn.disabled = !complete;
    atcBtn.setAttribute('aria-disabled', String(!complete));

    if (atcBtnText) {
      if (!complete) {
        atcBtnText.textContent = cms.atcIncomplete || 'Complete your kit to continue';
      } else if (state.mode === 'subscribe') {
        const label = cms.atcSubLabel || 'ADD TO BAG';
        const full  = cms.subPriceFull || '';
        const sale  = cms.subPriceSale || '';
        atcBtnText.innerHTML = `${label}&nbsp;|&nbsp;<s>${full}</s>&nbsp;${sale}`;
      } else {
        const label = cms.atcOtLabel || 'ADD TO BAG';
        const price = cms.otPrice    || '';
        atcBtnText.textContent = `${label} | ${price}`;
      }
    }

    if (atcNote) {
      if (complete && state.mode === 'subscribe') {
        atcNote.innerHTML = cms.atcSubNote || '';
      } else {
        atcNote.innerHTML = '';
      }
    }
  }

  // ─── Plan selection ────────────────────────────────────────────────────────
  planCards.forEach(card => {
    card.addEventListener('click', () => {
      const mode = card.dataset.mode;
      if (!mode || state.mode === mode) return;
      state.mode = mode;
      planCards.forEach(c => {
        c.classList.toggle('is-selected', c.dataset.mode === mode);
        const radio = c.querySelector('input[type="radio"]');
        if (radio) radio.checked = c.dataset.mode === mode;
      });
      render();
    });
  });

  // ─── Scrubber swatch selection ─────────────────────────────────────────────
  scrubberSwatches.forEach(swatch => {
    swatch.addEventListener('click', () => {
      if (swatch.dataset.available === 'false') return;
      scrubberSwatches.forEach(s => s.classList.remove('is-selected'));
      swatch.classList.add('is-selected');
      state.scrubberVariantId = swatch.dataset.variantId;
      state.scrubberPrice     = parseInt(swatch.dataset.variantPrice, 10) || 0;
      if (mainImage && swatch.dataset.fullres) mainImage.src = swatch.dataset.fullres;
      render();
    });
  });

  // ─── Soap tile interactions ────────────────────────────────────────────────
  soapTiles.forEach(tile => {
    const addBtn  = tile.querySelector('.sk-soap-tile__add-btn');
    const decrBtn = tile.querySelector('.sk-soap-tile__decrement');
    const incrBtn = tile.querySelector('.sk-soap-tile__increment');
    const imgWrap = tile.querySelector('.sk-soap-tile__image-wrap');

    function getSoapData() {
      return {
        variantId:     tile.dataset.variantId,
        productId:     tile.dataset.productId,
        sellingPlanId: tile.dataset.sellingPlanId || '',
        title:         tile.dataset.productTitle,
        price:         parseInt(tile.dataset.productPrice, 10) || 0,
        image:         tile.dataset.productImage
      };
    }

    function addSoap() {
      if (tile.classList.contains('sk-soap-tile--soldout')) return;
      if (soapCount() >= REQUIRED_SOAPS) return;
      const emptyIdx = state.soapSlots.findIndex(s => s === null);
      if (emptyIdx === -1) return;
      state.soapSlots[emptyIdx] = getSoapData();
      render();
    }

    function removeSoap() {
      const vid = tile.dataset.variantId;
      for (let i = state.soapSlots.length - 1; i >= 0; i--) {
        if (state.soapSlots[i] && state.soapSlots[i].variantId === vid) {
          state.soapSlots[i] = null;
          break;
        }
      }
      const filled = state.soapSlots.filter(Boolean);
      while (filled.length < REQUIRED_SOAPS) filled.push(null);
      state.soapSlots = filled;
      render();
    }

    if (addBtn)  addBtn.addEventListener('click',  e => { e.stopPropagation(); addSoap(); });
    if (incrBtn) incrBtn.addEventListener('click', e => { e.stopPropagation(); addSoap(); });
    if (decrBtn) decrBtn.addEventListener('click', e => { e.stopPropagation(); removeSoap(); });

    if (imgWrap) {
      imgWrap.addEventListener('click', e => {
        if (e.target.closest('.sk-soap-tile__controls')) return;
        if (tile.classList.contains('sk-soap-tile--soldout')) return;
        if (countForVariant(tile.dataset.variantId) > 0) return;
        addSoap();
      });
    }
  });

  // ─── Thumbnail gallery ─────────────────────────────────────────────────────
  thumbBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      thumbBtns.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      if (mainImage && btn.dataset.src) mainImage.src = btn.dataset.src;
      updateThumbProgress();
    });
  });

  function updateThumbProgress() {
    if (!thumbTrack || !thumbBar) return;
    const { scrollLeft, scrollWidth, clientWidth } = thumbTrack;
    const maxScroll  = scrollWidth - clientWidth;
    const pct        = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;
    const visibleFrac = clientWidth / scrollWidth;
    thumbBar.style.width = `${visibleFrac * 100}%`;
    thumbBar.style.left  = `${pct * (1 - visibleFrac)}%`;
  }

  if (thumbTrack) {
    thumbTrack.addEventListener('scroll', updateThumbProgress, { passive: true });
    updateThumbProgress();
    if (thumbBar) {
      const hidePb = () => {
        const { scrollWidth, clientWidth } = thumbTrack;
        const pbWrap = pdp.querySelector('.starter-kit-pdp__thumb-progress');
        if (pbWrap) pbWrap.style.display = scrollWidth <= clientWidth ? 'none' : '';
      };
      hidePb();
      window.addEventListener('resize', hidePb);
    }
  }

  // ─── Add to cart ──────────────────────────────────────────────────────────
  // Belt-and-suspenders guard: mark the button so no duplicate listener
  // can ever attach (Layer 2 sentinel above is the primary guard).
  if (atcBtn.dataset.skBound) {
    console.warn('[StarterKit] ATC already bound — skipping duplicate listener');
  } else {
    atcBtn.dataset.skBound = '1';

    let _atcBusy = false;

    atcBtn.addEventListener('click', async () => {
      if (_atcBusy) return;           // prevent double-click re-entry
      if (!isComplete()) return;
      _atcBusy = true;

      const { skScrubberVariantMap } = window.StarterKitData || {};

      // Resolve SK scrubber variant for subscribe mode
      let skScrubberVid = null;
      if (state.mode === 'subscribe') {
        const missingSoap = state.soapSlots.find(s => s && !parseInt(s.sellingPlanId, 10));
        if (missingSoap) {
          showAtcError('Subscription setup is incomplete for ' + (missingSoap.title || 'a soap') + '. Please refresh and try again.');
          _atcBusy = false;
          return;
        }
        const selectedSwatch = pdp.querySelector('.sk-color-swatch.is-selected');
        const swatchTitle    = selectedSwatch ? selectedSwatch.dataset.variantTitle : '';
        skScrubberVid        = skScrubberVariantMap && skScrubberVariantMap[swatchTitle];
        if (!skScrubberVid) {
          showAtcError('Could not resolve subscription scrubber variant. Please refresh and try again.');
          _atcBusy = false;
          return;
        }
      }

      atcBtn.disabled = true;
      atcBtnText.textContent = 'Adding…';

      const bundleKey = `${BUNDLE_KEY_PREFIX}-${Date.now()}`;

      try {
        const items = [];

        if (state.mode === 'subscribe') {
          items.push({
            id:         skScrubberVid,
            quantity:   1,
            properties: { '_bundle_key': bundleKey, '_bundle_label': 'scrubber', '_bundle_mode': 'subscribe' }
          });
          const soapMap = {};
          state.soapSlots.forEach(s => {
            if (!s) return;
            if (!soapMap[s.variantId]) {
              soapMap[s.variantId] = { qty: 0, sellingPlanId: parseInt(s.sellingPlanId, 10) };
            }
            soapMap[s.variantId].qty += 1;
          });
          Object.entries(soapMap).forEach(([id, { qty, sellingPlanId }]) => {
            items.push({
              id,
              quantity:      qty,
              selling_plan:  sellingPlanId,
              properties:    { '_bundle_key': bundleKey, '_bundle_label': 'soap', '_bundle_mode': 'subscribe' }
            });
          });

        } else {
          items.push({
            id:         state.scrubberVariantId,
            quantity:   1,
            properties: { '_bundle_key': bundleKey, '_bundle_label': 'scrubber', '_bundle_mode': 'onetime' }
          });
          const soapQtyMap = {};
          state.soapSlots.forEach(s => {
            if (s) soapQtyMap[s.variantId] = (soapQtyMap[s.variantId] || 0) + 1;
          });
          Object.entries(soapQtyMap).forEach(([id, qty]) => {
            items.push({
              id,
              quantity:   qty,
              properties: { '_bundle_key': bundleKey, '_bundle_label': 'soap', '_bundle_mode': 'onetime' }
            });
          });
        }

        const res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ items })
        });
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);

        // Apply discount(s) for subscribe mode (must complete before drawer render
        // so the Section API returns HTML with strikethrough pricing). Codes come
        // from the section CMS — array of [priceCode, shippingCode]; either may be blank.
        if (state.mode === 'subscribe' && window._skApplyDiscount) {
          await window._skApplyDiscount([cms.discountCode, cms.shippingDiscountCode]);
        }

        const sections = await fetch('/cart?sections=cart-drawer,cart-icon-bubble')
          .then(r => r.json())
          .catch(() => null);

        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl && typeof drawerEl.renderContents === 'function' && sections) {
          if (window._skSetSuppressed) window._skSetSuppressed(true);
          drawerEl.classList.remove('is-empty');
          drawerEl.renderContents({ sections });
          setTimeout(() => {
            if (window._skInjectKitCard) window._skInjectKitCard();
            setTimeout(() => {
              if (window._skSetSuppressed) window._skSetSuppressed(false);
              _atcBusy = false;
            }, 1000);
          }, 250);
        } else {
          window.location.href = '/cart';
        }

        // Reset PDP selections so user can build another kit
        state.soapSlots = [null, null, null];
        render();
      } catch (err) {
        console.error('[StarterKit] ATC error:', err);
        showAtcError('Something went wrong adding your kit. Please try again.');
        _atcBusy = false;
        render();
      }
    });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────
  render();
})();
